import { DOCUMENT, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import {
    LucideBot,
    LucideBriefcaseBusiness,
    LucideBuilding2,
    LucideChartBar,
    LucideChartNoAxesCombined,
    LucideChevronsUpDown,
    LucideCircleUserRound,
    LucideCloud,
    LucideDynamicIcon,
    LucideEuro,
    LucideFileText,
    LucideHouse,
    LucideIcon,
    LucideIconData,
    LucideLaptopMinimal,
    LucideLayoutGrid,
    LucideMapPinned,
    LucideNotebookText,
    LucidePanelLeftClose,
    LucidePanelLeftOpen,
    LucideReceiptText,
    LucideSprout,
    LucideUser,
    LucideUsers,
    LucideWifi,
} from '@lucide/angular';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';
import { injectRouteData } from 'ngxtension/inject-route-data';
import { ButtonDirective } from 'primeng/button';
import { ClassNames } from 'primeng/classnames';
import { Divider } from 'primeng/divider';
import { Popover } from 'primeng/popover';
import { SelectButton } from 'primeng/selectbutton';
import { Splitter } from 'primeng/splitter';
import { Tooltip } from 'primeng/tooltip';
import { filter } from 'rxjs';

import {
    AdvaniaSmallLogo,
    BreadcrumbComponent,
    EnvironmentService,
    MicrosoftLogo,
    SidebarBodyComponent,
    SidebarBodyHeaderComponent,
    SidebarBodyItemComponent,
    SidebarComponent,
    SidebarExpandedDirective,
    SidebarFooterComponent,
    SidebarHeaderComponent,
    SidebarIconDirective,
} from '@advania-cosoftware/advania-styles';
import { url } from '@advania-cosoftware/utils';
import {
    CustomerContextService,
    SessionService,
    SessionState,
    translatedObjectSignal,
} from '@advania-cosoftware/wolf-core';
import {
    AccountsRouter,
    AdvaniaServicesRouter,
    AiRouter,
    ApplicationsRouter,
    AzureTenantsRouter,
    DevicesRouter,
    DocumentsRouter,
    HomeRouter,
    ItClimateRouter,
    ItFinancialsRouter,
    LocationsRouter,
    MicrosoftLicensesRouter,
    MicrosoftServicesRouter,
    NetworkRouter,
    PricelistManagementRouter,
    ProjectsRouter,
} from '@advania-cosoftware/wolf-router';

import { DomainProfileService } from '../../../environment/domain-profile.service';
import { UserProfileComponent } from '../../../profile/user-profile/user-profile.component';
import { CustomerSelectorComponent } from '../../customer/customer-selector/customer-selector.component';
import { AuthenticatedSessionText, authenticatedSessionTextKey } from './authenticated-session.translations';
import { showItFinancialsV2Navigation } from './it-financial-v2-navigation';
import { WorkspaceNavigationService } from './workspace-navigation.service';

const Workspace = {
    Customer: 'customer',
    Internal: 'internal',
} as const;
type Workspace = (typeof Workspace)[keyof typeof Workspace];

type SidebarItem = {
    readonly id: string;
    readonly label: string;
    readonly icon: LucideIcon | LucideIconData;
    readonly route: (string | number)[];
    readonly shortcut?: string;
    readonly tooltip: string;
    readonly disabled?: boolean;
};

type SidebarSection = {
    readonly label: string;
    readonly items: SidebarItem[];
};

@Component({
    selector: 'app-authenticated-session',
    templateUrl: './authenticated-session.layout.html',
    imports: [
        SidebarComponent,
        SidebarHeaderComponent,
        SidebarBodyComponent,
        SidebarBodyHeaderComponent,
        SidebarBodyItemComponent,
        SidebarFooterComponent,
        SidebarExpandedDirective,
        SidebarIconDirective,
        FormsModule,
        ButtonDirective,
        SelectButton,
        Splitter,
        RouterOutlet,
        Popover,
        UserProfileComponent,
        CustomerSelectorComponent,
        NgOptimizedImage,
        ClassNames,
        Divider,
        Tooltip,
        BreadcrumbComponent,
        LucideDynamicIcon,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(window:keydown)': 'shortcutListener($event)',
    },
})
export class AuthenticatedSessionLayout {
    protected readonly SessionState = SessionState;
    protected readonly Workspace = Workspace;
    private readonly _environment = inject(EnvironmentService);
    private readonly _domainProfile = inject(DomainProfileService);
    private readonly _session = inject(SessionService);
    private readonly _document = inject(DOCUMENT);
    private readonly _customerContext = inject(CustomerContextService);
    private readonly _advaniaServicesRouter = inject(AdvaniaServicesRouter);
    private readonly _accountsRouter = inject(AccountsRouter);
    private readonly _aiRouter = inject(AiRouter);
    private readonly _pricelistRouter = inject(PricelistManagementRouter);
    private readonly _applicationsRouter = inject(ApplicationsRouter);
    private readonly _azureTenantsRouter = inject(AzureTenantsRouter);
    private readonly _devicesRouter = inject(DevicesRouter);
    private readonly _documentsRouter = inject(DocumentsRouter);
    private readonly _itClimateRouter = inject(ItClimateRouter);
    private readonly _itFinancialsRouter = inject(ItFinancialsRouter);
    private readonly _locationsRouter = inject(LocationsRouter);
    private readonly _microsoftLicensesRouter = inject(MicrosoftLicensesRouter);
    private readonly _microsoftServicesRouter = inject(MicrosoftServicesRouter);
    private readonly _networkRouter = inject(NetworkRouter);
    private readonly _projectsRouter = inject(ProjectsRouter);
    private readonly _homeRouter = inject(HomeRouter);
    private readonly _router = inject(Router);
    private readonly _workspaceNavigation = inject(WorkspaceNavigationService);

    protected readonly sidebarTitleText = 'Advania Wolf';
    protected readonly accentClass = this._domainProfile.accentClass;
    protected readonly UserIcon = LucideCircleUserRound;
    protected readonly Chevrons = LucideChevronsUpDown;
    protected readonly showShortcuts = !this._environment.isMobilePlatform;
    protected readonly currentUrl = url();
    protected readonly isItFinancialsV2Route = computed(() => {
        const base = `/${this._itFinancialsRouter.getItFinancialsV2Route().slice(1).join('/')}`;
        const path = this.currentUrl().split(/[?#]/, 1)[0];
        return path === base || path.startsWith(`${base}/`);
    });
    protected readonly workspace = injectRouteData.global<Workspace>(
        (data) => (data['workspace'] === Workspace.Internal ? Workspace.Internal : Workspace.Customer),
        { defaultValue: Workspace.Customer },
    );
    protected readonly activeCustomerNumber = this._session.activeCustomerNumber;
    protected readonly activeCustomerName = computed(() => this._session.activeCustomer()?.customerName ?? '');
    protected readonly user = this._session.user;
    protected readonly isInternalUser = computed(() => this.user()?.isInternal ?? false);
    protected readonly canAccessItFinancialsV2 = computed(() =>
        showItFinancialsV2Navigation(
            this.user(),
            this._document.location.hostname,
            this._customerContext.features()?.itFinancials ?? false,
        ),
    );
    protected readonly sessionState = this._session.state;
    protected readonly isMobileLayout = this._environment.isMobileLayout;
    protected readonly mobileOpen = signal(false);
    private readonly _text = translatedObjectSignal<AuthenticatedSessionText>(authenticatedSessionTextKey);
    protected readonly modeOptions = computed(() => [
        {
            value: Workspace.Customer,
            label: this._text().Mode.Customer,
            icon: LucideBuilding2,
        },
        {
            value: Workspace.Internal,
            label: this._text().Mode.Internal,
            icon: LucideBriefcaseBusiness,
        },
    ]);
    protected readonly modeLabel = computed(() => this._text().Mode.Label);
    protected readonly workspaceShortcut = computed(() => `${this._environment.UnicodePlatformKeys?.alt}+Shift+W`);
    protected readonly workspaceAriaShortcut = 'Alt+Shift+W';
    protected readonly workspaceTooltip = computed(() => `${this.modeToggleLabel()} (${this.workspaceShortcut()})`);
    protected readonly modeToggleIcon = computed(() =>
        this.workspace() === Workspace.Internal ? LucideBriefcaseBusiness : LucideBuilding2,
    );
    protected readonly modeToggleLabel = computed(() =>
        this.workspace() === Workspace.Internal
            ? this._text().Mode.SwitchToCustomer
            : this._text().Mode.SwitchToInternal,
    );
    protected readonly internalName = computed(() => this._text().Internal.Name);
    protected readonly internalDescription = computed(() => this._text().Internal.Description);
    protected readonly sidebarOpen = computed(() => (this.isMobileLayout() ? this.mobileOpen() : this.expanded()));
    protected readonly sidebarIcon = computed<LucideIcon>(() => {
        if (this.sidebarOpen()) {
            return LucidePanelLeftClose;
        } else {
            return LucidePanelLeftOpen;
        }
    });

    expanded = injectLocalStorage<boolean>('sidebar-expanded', {
        defaultValue: () => true,
        storageSync: true,
    });

    constructor() {
        effect(() => this._workspaceNavigation.remember(this.currentUrl()));
        this._router.events
            .pipe(
                filter((event): event is NavigationStart => event instanceof NavigationStart),
                takeUntilDestroyed(),
            )
            .subscribe(() => this._workspaceNavigation.remember(this._router.url));
    }

    protected readonly customerSelectorText = computed(() => this._text().Actions.CustomerSelector);
    protected readonly userProfileText = computed(() => this._text().Actions.UserProfile);
    protected readonly toggleSidebarText = computed(() => this._text().Actions.ToggleSidebar);

    // Computed Tooltips with shortcuts
    protected readonly toggleSidebarTooltip = computed<string>(() => {
        return `${this.toggleSidebarText()} (${this._environment.UnicodePlatformKeys?.meta} + B)`;
    });
    protected readonly homeTooltip = computed<string>(() => {
        return `${this._text().Actions.Home} (${this._environment.UnicodePlatformKeys?.alt} + H)`;
    });

    async goHome(): Promise<void> {
        await this._router.navigate(
            this.workspace() === Workspace.Internal ? this._aiRouter.getAiChatRoute() : this._homeRouter.getHomeRoute(),
        );
    }

    async goApplications(): Promise<void> {
        await this._router.navigate(this._applicationsRouter.getApplicationsRoute());
    }

    async goAzureTenants(): Promise<void> {
        const availableFeatures = this._customerContext.features();
        if (availableFeatures?.azure) {
            await this._router.navigate(this._azureTenantsRouter.getAzureTenantsRoute());
        }
    }

    async goDevices(): Promise<void> {
        if (!this.currentUrl().includes('/devices')) {
            await this._router.navigate(this._devicesRouter.getDevicesRoute());
        }
    }

    async goLocations(): Promise<void> {
        const availableFeatures = this._customerContext.features();
        if (availableFeatures?.locations) {
            await this._router.navigate(this._locationsRouter.getLocationsRoute());
        }
    }

    async goMicrosoftLicenses(): Promise<void> {
        const availableFeatures = this._customerContext.features();
        if (availableFeatures?.microsoftLicenses) {
            await this._router.navigate(this._microsoftLicensesRouter.getMicrosoftLicensesRoute());
        }
    }
    async goNetwork(): Promise<void> {
        if (!this.currentUrl().includes('/networks')) {
            await this._router.navigate(this._networkRouter.getNetworkRoute());
        }
    }

    async goAdvaniaServices(): Promise<void> {
        await this._router.navigate(this._advaniaServicesRouter.getAdvaniaServicesRoute());
    }

    async goMicrosoftServices(): Promise<void> {
        await this._router.navigate(this._microsoftServicesRouter.getMicrosoftServicesRoute());
    }

    async goItFinancials(): Promise<void> {
        const availableFeatures = this._customerContext.features();
        if (availableFeatures?.itFinancials && !this.currentUrl().includes('/it-financials')) {
            await this._router.navigate(this._itFinancialsRouter.getItFinancialsRoute());
        }
    }

    async goItClimate(): Promise<void> {
        const availableFeatures = this._customerContext.features();
        if (availableFeatures?.carbonEmissions) {
            await this._router.navigate(this._itClimateRouter.getItClimateRoute());
        }
    }

    async goUsers(): Promise<void> {
        await this._router.navigate(this._accountsRouter.getAccountsRoute());
    }

    async goUserGroups(): Promise<void> {
        await this._router.navigate(this._accountsRouter.getGroupsRoute());
    }

    async goReleaseNotes(): Promise<void> {
        await this._router.navigate(this._documentsRouter.getReleaseNotesRoute());
    }

    async goWikiArticles(): Promise<void> {
        await this._router.navigate(this._documentsRouter.getWikiArticlesRoute());
    }

    async goProjects(): Promise<void> {
        const availableFeatures = this._customerContext.features();
        if (availableFeatures?.dynamicsProjects) {
            await this._router.navigate(this._projectsRouter.getProjectsRoute());
        }
    }

    async goPricelistManagement(): Promise<void> {
        if (!this.isInternalUser()) {
            return;
        }

        await this._router.navigate(this._pricelistRouter.getPricelistManagementRoute());
    }

    protected readonly sidebarSections = computed<SidebarSection[]>(() => {
        const availableFeatures = this._customerContext.features();
        if (!availableFeatures) {
            return [];
        }
        const text = this._text();
        const askToEnable = text.Actions.AskToEnable;
        return [
            {
                label: text.Sections.Inventory,
                items: [
                    {
                        label: text.Navigation.Applications.Label,
                        icon: LucideLayoutGrid,
                        route: this._applicationsRouter.getApplicationsRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+A`,
                        tooltip: text.Navigation.Applications.Tooltip,
                        id: 'Applications',
                    },
                    {
                        label: text.Navigation.Devices.Label,
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+C`,
                        icon: LucideLaptopMinimal,
                        route: this._devicesRouter.getDevicesRoute(),
                        tooltip: text.Navigation.Devices.Tooltip,
                        id: 'Devices',
                    },
                    {
                        label: text.Navigation.Locations.Label,
                        icon: LucideMapPinned,
                        route: this._locationsRouter.getLocationsRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+L`,
                        disabled: !availableFeatures.locations,
                        tooltip: availableFeatures.locations
                            ? text.Navigation.Locations.Tooltip
                            : `${text.Navigation.Locations.Disabled} - ${askToEnable}`,
                        id: 'Locations',
                    },
                    {
                        label: text.Navigation.Network.Label,
                        icon: LucideWifi,
                        route: this._networkRouter.getNetworkRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+N`,
                        disabled: !availableFeatures.networking,
                        tooltip: availableFeatures.networking
                            ? text.Navigation.Network.Tooltip
                            : `${text.Navigation.Network.Disabled} - ${askToEnable}`,
                        id: 'Network',
                    },
                    {
                        label: text.Navigation.AzureTenants.Label,
                        icon: LucideCloud,
                        route: this._azureTenantsRouter.getAzureTenantsRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+Z`,
                        disabled: !availableFeatures.azure,
                        tooltip: availableFeatures.azure
                            ? text.Navigation.AzureTenants.Tooltip
                            : `${text.Navigation.AzureTenants.Disabled} - ${askToEnable}`,
                        id: 'AzureTenants',
                    },
                    {
                        label: text.Navigation.MicrosoftLicenses.Label,
                        icon: LucideReceiptText,
                        route: this._microsoftLicensesRouter.getMicrosoftLicensesRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+M`,
                        disabled: !availableFeatures.microsoftLicenses,
                        tooltip: availableFeatures.microsoftLicenses
                            ? text.Navigation.MicrosoftLicenses.Tooltip
                            : `${text.Navigation.MicrosoftLicenses.Disabled} - ${askToEnable}`,
                        id: 'MicrosoftLicences',
                    },
                    {
                        label: text.Navigation.UserAccounts.Label,
                        icon: LucideUser,
                        route: this._accountsRouter.getAccountsRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+U`,
                        tooltip: text.Navigation.UserAccounts.Tooltip,
                        id: 'UserAccounts',
                    },
                    {
                        label: text.Navigation.UserGroups.Label,
                        icon: LucideUsers,
                        route: this._accountsRouter.getGroupsRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+G`,
                        tooltip: text.Navigation.UserGroups.Tooltip,
                        id: 'UserGroups',
                    },
                ],
            },
            {
                label: text.Sections.Services,
                items: [
                    {
                        label: text.Navigation.AdvaniaServices.Label,
                        icon: AdvaniaSmallLogo,
                        route: this._advaniaServicesRouter.getAdvaniaServicesRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+V`,
                        tooltip: text.Navigation.AdvaniaServices.Tooltip,
                        id: 'AdvaniaServices',
                    },
                    {
                        label: text.Navigation.MicrosoftServices.Label,
                        icon: MicrosoftLogo,
                        route: this._microsoftServicesRouter.getMicrosoftServicesRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+I`,
                        tooltip: text.Navigation.MicrosoftServices.Tooltip,
                        id: 'MicrosoftServices',
                    },
                ],
            },
            {
                label: text.Sections.Governance,
                items: [
                    {
                        label: text.Navigation.Projects.Label,
                        icon: LucideChartBar,
                        route: this._projectsRouter.getProjectsRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+P`,
                        disabled: !availableFeatures.dynamicsProjects,
                        tooltip: availableFeatures.dynamicsProjects
                            ? text.Navigation.Projects.Tooltip
                            : `${text.Navigation.Projects.Disabled} - ${askToEnable}`,
                        id: 'Projects',
                    },
                    //This is temporary logic to not show ItFinancials when customers don't have it.
                    ...(availableFeatures.itFinancials
                        ? [
                              {
                                  label: text.Navigation.ItFinancials.Label,
                                  icon: LucideChartNoAxesCombined,
                                  route: this._itFinancialsRouter.getItFinancialsRoute(),
                                  shortcut: `${this._environment.UnicodePlatformKeys?.alt}+F`,
                                  disabled: !availableFeatures.itFinancials,
                                  tooltip: availableFeatures.itFinancials
                                      ? text.Navigation.ItFinancials.Tooltip
                                      : `${text.Navigation.ItFinancials.Disabled} - ${askToEnable}`,
                                  id: 'ItFinancials',
                              },
                              ...(this.canAccessItFinancialsV2()
                                  ? [
                                        {
                                            label: text.Navigation.ItFinancialsV2.Label,
                                            icon: LucideChartNoAxesCombined,
                                            route: this._itFinancialsRouter.getItFinancialsV2Route(),
                                            tooltip: text.Navigation.ItFinancialsV2.Tooltip,
                                            id: 'ItFinancialsV2',
                                        },
                                    ]
                                  : []),
                          ]
                        : []),
                    {
                        label: text.Navigation.ItClimate.Label,
                        icon: LucideSprout,
                        route: this._itClimateRouter.getItClimateRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+E`,
                        disabled: !availableFeatures.carbonEmissions,
                        tooltip: availableFeatures.carbonEmissions
                            ? text.Navigation.ItClimate.Tooltip
                            : `${text.Navigation.ItClimate.Disabled} - ${askToEnable}`,
                        id: 'ItClimate',
                    },
                ],
            },
            {
                label: text.Sections.Documents,
                items: [
                    {
                        label: text.Navigation.ReleaseNotes.Label,
                        icon: LucideNotebookText,
                        route: this._documentsRouter.getReleaseNotesRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+R`,
                        tooltip: text.Navigation.ReleaseNotes.Tooltip,
                        id: 'ReleaseNotes',
                    },
                    {
                        label: text.Navigation.WikiArticles.Label,
                        icon: LucideFileText,
                        route: this._documentsRouter.getWikiArticlesRoute(),
                        shortcut: `${this._environment.UnicodePlatformKeys?.alt}+W`,
                        tooltip: text.Navigation.WikiArticles.Tooltip,
                        id: 'WikiArticles',
                    },
                ],
            },
        ];
    });

    protected readonly internalSidebarSections = computed<SidebarSection[]>(() => [
        {
            label: this._text().Internal.Section,
            items: [
                {
                    label: this._text().Internal.AiChat,
                    icon: LucideBot,
                    route: this._aiRouter.getAiChatRoute(),
                    shortcut: `${this._environment.UnicodePlatformKeys?.alt}+Q`,
                    tooltip: this._text().Internal.AiChatTooltip,
                    id: 'InternalAiChat',
                },
                {
                    label: this._text().Internal.PricelistName,
                    icon: LucideEuro,
                    route: this._pricelistRouter.getPricelistManagementRoute(),
                    shortcut: `${this._environment.UnicodePlatformKeys?.alt}+4`,
                    tooltip: this._text().Internal.PricelistTooltip,
                    id: 'InternalPricelistManagement',
                },
            ],
        },
    ]);

    home = computed(() => {
        const route =
            this.workspace() === Workspace.Internal ? this._aiRouter.getAiChatRoute() : this._homeRouter.getHomeRoute();
        return {
            label: this._text().Actions.Home,
            routerLink: route,
            url: '/' + route.filter((s) => s !== '/').join('/'),
            lucideIcon: LucideHouse,
            tooltip: this.homeTooltip(),
        };
    });

    toggleSidebar() {
        if (this.isMobileLayout()) {
            this.mobileOpen.update((open) => !open);
            return;
        }

        this.expanded.update((expanded) => !expanded);
    }

    protected switchWorkspace(workspace: Workspace): void {
        if (!this.isInternalUser()) {
            return;
        }

        if (workspace === this.workspace()) {
            return;
        }

        this._workspaceNavigation.remember(this._router.url);
        const lastUrl = this._workspaceNavigation.lastUrl(workspace);
        if (lastUrl) {
            void this._router.navigateByUrl(lastUrl);
            return;
        }

        if (workspace === Workspace.Internal) {
            void this._router.navigate(this._aiRouter.getAiChatRoute());
            return;
        }

        const customerNumber = this._session.previousCustomerNumber();
        void this._router.navigate(customerNumber ? ['/', customerNumber, 'home'] : ['/']);
    }

    private async goAiChat(): Promise<void> {
        if (!this.isInternalUser()) {
            return;
        }

        await this._router.navigate(this._aiRouter.getAiChatRoute());
    }

    async shortcutListener(event: KeyboardEvent): Promise<void> {
        // Early return if neither ctrl/cmd nor option/alt is used
        if (!event.metaKey && !event.ctrlKey && !event.altKey) {
            return;
        }

        const isCmdOrCtrl = event.metaKey || event.ctrlKey;

        // Cmd/Ctrl + B
        if (isCmdOrCtrl && event.code === 'KeyB') {
            event.preventDefault();
            this.toggleSidebar();
        }

        // Alt/Option + Shift + W
        if (event.altKey && event.shiftKey && event.code === 'KeyW') {
            event.preventDefault();
            this.switchWorkspace(this.workspace() === Workspace.Internal ? Workspace.Customer : Workspace.Internal);
            return;
        }

        // Alt/Option + Q
        if (event.altKey && event.code === 'KeyQ') {
            event.preventDefault();
            await this.goAiChat();
            return;
        }

        // Alt/Option + H
        if (event.altKey && event.code === 'KeyH') {
            event.preventDefault();
            await this.goHome();
        }

        // Alt/Option + A
        if (event.altKey && event.code === 'KeyA') {
            event.preventDefault();
            await this.goApplications();
        }
        // Alt/Option + Z
        if (event.altKey && event.code === 'KeyZ') {
            event.preventDefault();
            await this.goAzureTenants();
        }

        // Alt/Option + C
        if (event.altKey && event.code === 'KeyC') {
            event.preventDefault();
            await this.goDevices();
        }

        // Alt/Option + L
        if (event.altKey && event.code === 'KeyL') {
            event.preventDefault();
            await this.goLocations();
        }

        // Alt/Option + M
        if (event.altKey && event.code === 'KeyM') {
            event.preventDefault();
            await this.goMicrosoftLicenses();
        }

        // Alt/Option + N
        if (event.altKey && event.code === 'KeyN') {
            await this.goNetwork();
        }

        // Alt/Option + V
        if (event.altKey && event.code === 'KeyV') {
            event.preventDefault();
            await this.goAdvaniaServices();
        }

        // Alt/Option + I
        if (event.altKey && event.code === 'KeyI') {
            event.preventDefault();
            await this.goMicrosoftServices();
        }

        // Alt/Option + F
        if (event.altKey && event.code === 'KeyF') {
            event.preventDefault();
            await this.goItFinancials();
        }

        // Alt/Option + E
        if (event.altKey && event.code === 'KeyE') {
            event.preventDefault();
            await this.goItClimate();
        }

        // Alt/Option + U
        if (event.altKey && event.code === 'KeyU') {
            event.preventDefault();
            await this.goUsers();
        }

        // Alt/Option + G
        if (event.altKey && event.code === 'KeyG') {
            event.preventDefault();
            await this.goUserGroups();
        }

        // Alt/Option + R
        if (event.altKey && event.code === 'KeyR') {
            event.preventDefault();
            await this.goReleaseNotes();
        }

        // Alt/Option + W
        if (event.altKey && event.code === 'KeyW') {
            event.preventDefault();
            await this.goWikiArticles();
        }

        // Alt/Option + P
        if (event.altKey && event.code === 'KeyP') {
            event.preventDefault();
            await this.goProjects();
        }

        if (event.altKey && event.code === 'Digit4') {
            event.preventDefault();
            await this.goPricelistManagement();
        }
    }
}
